import React, { createContext, useState, useEffect, useContext, ReactNode } from "react";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "../firebase";
import { CollabDataContext } from "./collabDataContext";
import { Campaign } from "@foodfluence/shared";

interface CampaignDataContextType {
    [collabId: string]: Campaign
}

export const InfluencerCampaignDataContext = createContext<CampaignDataContextType>({});

export const InfluencerCampaignDataLoadingContext = createContext<boolean>(false);

interface InfluencerCampaignDataProviderProps {
    children: ReactNode;
}

export const InfluencerCampaignDataProvider: React.FC<InfluencerCampaignDataProviderProps> = ({ children }) => {
    const collabData = useContext(CollabDataContext);
    const [campaignData, setCampaignData] = useState<CampaignDataContextType>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (collabData.length === 0) {
            setCampaignData({});
            setLoading(false);
            return;
        }

        setLoading(true);
        const fetchCampaignData = async () => {
            const campaignPromises = collabData.map(async (collab) => {
                if (collab.influencerCampaignID) {
                    const campaignDocRef = doc(firestore, "influencerCampaigns", collab.influencerCampaignID);
                    const campaignDocSnap = await getDoc(campaignDocRef);
                    if (campaignDocSnap.exists()) {
                        const campaignData = campaignDocSnap.data() as Campaign;
                        campaignData.id = campaignDocSnap.id;
                        return {
                            [collab.id]: campaignData
                        };
                    }
                }
                return null;
            });

            const campaignResults = await Promise.all(campaignPromises);
            const campaigns = campaignResults.reduce((acc, result) => {
                if (result) {
                    return { ...acc, ...result };
                }
                return acc;
            }, {});

            setCampaignData(campaigns);
        };

        fetchCampaignData().finally(() => setLoading(false));
    }, [collabData]);

    return (
        <InfluencerCampaignDataContext.Provider value={campaignData}>
            <InfluencerCampaignDataLoadingContext.Provider value={loading}>
                {children}
            </InfluencerCampaignDataLoadingContext.Provider>
        </InfluencerCampaignDataContext.Provider>
    );
};